
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Story, Category } from "@/types";
import { createSlug, getCategoryName } from "@/utils/categoryUtils";

export const useFeaturedStories = (limit: number = 8) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFeaturedStories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch stories with the most views
        const { data: storiesData, error: storiesError } = await supabase
          .from('stories')
          .select('*')
          .order('views', { ascending: false })
          .limit(limit);
          
        if (storiesError) {
          throw storiesError;
        }
        
        if (storiesData && storiesData.length > 0) {
          // Get author profiles for the stories
          const authorIds = [...new Set(storiesData.map(story => story.author_id))];
          
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('*')
            .in('id', authorIds);
            
          if (profilesError) {
            throw profilesError;
          }

          // Get categories for the stories
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('story_categories')
            .select('*');
            
          if (categoriesError) {
            throw categoriesError;
          }
          
          // Map of story id to categories
          const storyCategoriesMap = new Map<string, Category[]>();
          storiesData.forEach(story => {
            storyCategoriesMap.set(story.id, []);
          });
          
          // Get unique category IDs
          const uniqueCategoryIds = new Set<string>();
          categoriesData?.forEach(categoryItem => {
            uniqueCategoryIds.add(String(categoryItem.category_id));
          });
          
          // Create a map of category id to category info
          const categoryInfoMap = new Map<string, Category>();
          uniqueCategoryIds.forEach(id => {
            const categoryName = getCategoryName(id);
            const categorySlug = createSlug(categoryName);
            
            categoryInfoMap.set(id, {
              id,
              name: categoryName,
              slug: categorySlug
            });
          });
          
          // Assign categories to stories
          categoriesData?.forEach(categoryItem => {
            if (storyCategoriesMap.has(categoryItem.story_id)) {
              const categoryInfo = categoryInfoMap.get(String(categoryItem.category_id));
              if (categoryInfo) {
                const categories = storyCategoriesMap.get(categoryItem.story_id);
                if (categories) {
                  categories.push(categoryInfo);
                }
              }
            }
          });

          // Create map of author ids to author profiles
          const profilesMap = new Map();
          profilesData?.forEach(profile => {
            profilesMap.set(profile.id, profile);
          });
          
          // Format the stories with author and category info
          const formattedStories: Story[] = storiesData.map(story => {
            const authorProfile = profilesMap.get(story.author_id);
            
            return {
              id: story.id,
              title: story.title,
              description: story.description,
              coverImage: story.cover_image_url || 'https://placehold.co/600x400?text=No+Image',
              status: story.status as "ongoing" | "completed" | "hiatus",
              premium: story.is_premium,
              updateSchedule: story.update_schedule || 'Weekly',
              views: story.views || 0,
              likes: story.likes || 0,
              bookmarks: story.bookmarks || 0,
              totalEpisodes: story.total_episodes || 0,
              publishedDate: story.published_date,
              author: {
                id: story.author_id,
                name: authorProfile?.full_name || 'Unknown Author',
                username: authorProfile?.username || 'unknown',
                bio: authorProfile?.bio || '',
                avatarUrl: authorProfile?.avatar_url || '',
                followers: 0,
                following: 0,
                joinedDate: authorProfile?.created_at || new Date().toISOString(),
                isPremium: false,
                isAuthor: authorProfile?.is_author || false
              },
              categories: storyCategoriesMap.get(story.id) || [],
              tags: [], // Empty array for tags as per the Story type definition
              episodes: []
            };
          });
          
          setStories(formattedStories);
        }
      } catch (error) {
        console.error("Error fetching featured stories:", error);
        setError(error instanceof Error ? error : new Error('Failed to fetch featured stories'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFeaturedStories();
  }, [limit]);

  return {
    stories,
    isLoading,
    error
  };
};
